import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes, faFileLines, faArrowUpFromBracket, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Button, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import './ConfirmImportModal.css';

/**
 * Shared confirmation step shown when a picker selection contains a folder
 * (Google Drive / OneDrive) or a Notion database. The parent resolves the
 * selection into a flat file list on the backend, then shows it here so the
 * user can review, check/uncheck individual files (and select/deselect all),
 * and import only what they keep. `onConfirm` receives the checked files.
 */
function ConfirmImportModal({
  open,
  onClose,
  onConfirm,
  files = [],
  loading = false,
  error = false,
  remainingSlots = Infinity,
  onUpgrade,
  importing = false,
}) {
  const { t } = useTranslation();
  const keyOf = (f, i) => (f && f.id != null ? f.id : `idx-${i}`);

  const [selectedKeys, setSelectedKeys] = useState(() => new Set());

  // Default to everything selected whenever the resolved list changes.
  useEffect(() => {
    setSelectedKeys(new Set(files.map((f, i) => keyOf(f, i))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  if (!open) return null;

  const selectedFiles = files.filter((f, i) => selectedKeys.has(keyOf(f, i)));
  const selectedCount = selectedFiles.length;
  const total = files.length;
  const allSelected = total > 0 && selectedCount === total;
  const someSelected = selectedCount > 0 && selectedCount < total;
  const overLimit = selectedCount > remainingSlots;

  const toggle = (key) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelectedKeys(new Set());
    else setSelectedKeys(new Set(files.map((f, i) => keyOf(f, i))));
  };

  const checkboxSx = {
    color: '#5616ea',
    '&.Mui-checked': { color: '#5616ea' },
    '&.MuiCheckbox-indeterminate': { color: '#5616ea' },
    padding: '4px',
  };

  return (
    <div className="confirm-import-overlay" onClick={onClose}>
      <div className="confirm-import-modal" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-import-header">
          <h2>{t('confirm_import_title') || 'Review files to import'}</h2>
          <button className="confirm-import-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {loading ? (
          <div className="confirm-import-loading">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#5616ea' }} />
            <p>{t('confirm_import_resolving') || 'Collecting all files from your selection…'}</p>
          </div>
        ) : error ? (
          <>
            <div className="confirm-import-warning">
              <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: 8 }} />
              {t('confirm_import_error') || 'Could not read your selection — it may be very large or a temporary connection issue. Please try again.'}
            </div>
            <div className="confirm-import-actions">
              <Button
                onClick={onClose}
                variant="outlined"
                sx={{ color: '#5616ea', borderColor: '#5616ea', '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' } }}
              >
                {t('cancel')}
              </Button>
            </div>
          </>
        ) : total === 0 ? (
          <>
            <div className="confirm-import-empty">
              {t('confirm_import_empty') || 'No importable files were found in your selection.'}
            </div>
            <div className="confirm-import-actions">
              <Button
                onClick={onClose}
                variant="outlined"
                sx={{ color: '#5616ea', borderColor: '#5616ea', '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' } }}
              >
                {t('cancel')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="confirm-import-subtitle">
              {selectedCount} {selectedCount === 1 ? (t('confirm_import_file') || 'file') : (t('confirm_import_files') || 'files')}{' '}
              {t('confirm_import_will_add') || 'will be added to your knowledge base'}.
            </p>

            <div className="confirm-import-list">
              <label className="confirm-import-selectall">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                  sx={checkboxSx}
                />
                {t('select_all') || 'Select all'} ({total})
              </label>

              {files.map((f, i) => {
                const key = keyOf(f, i);
                return (
                  <label key={key} className="confirm-import-row">
                    <Checkbox
                      checked={selectedKeys.has(key)}
                      onChange={() => toggle(key)}
                      sx={checkboxSx}
                    />
                    <FontAwesomeIcon icon={faFileLines} className="confirm-import-row-icon" />
                    <span className="confirm-import-row-name">{f.name || t('untitled')}</span>
                  </label>
                );
              })}
            </div>

            {overLimit && (
              <div className="confirm-import-warning">
                <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: 8 }} />
                {(t('confirm_import_over_limit') || 'This is more than your plan allows. You have {n} slot(s) left.').replace('{n}', remainingSlots)}
              </div>
            )}

            <div className="confirm-import-actions">
              <Button
                onClick={onClose}
                variant="outlined"
                sx={{ color: '#5616ea', borderColor: '#5616ea', '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' } }}
              >
                {t('cancel')}
              </Button>
              {overLimit ? (
                <Button
                  onClick={onUpgrade}
                  variant="contained"
                  sx={{ backgroundColor: '#5616ea', '&:hover': { backgroundColor: '#4a14d6' } }}
                >
                  {t('upgrade') || 'Upgrade'}
                </Button>
              ) : (
                <Button
                  onClick={() => onConfirm(selectedFiles)}
                  variant="contained"
                  disabled={selectedCount === 0 || importing}
                  sx={{
                    backgroundColor: '#5616ea',
                    '&:hover': { backgroundColor: '#4a14d6' },
                    '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' },
                  }}
                >
                  {importing ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} />
                      {t('importing') || 'Importing...'}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faArrowUpFromBracket} style={{ marginRight: 8 }} />
                      {(t('confirm_import_button') || 'Import {n} files').replace('{n}', selectedCount)}
                    </>
                  )}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ConfirmImportModal;
